import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ExternalLink, GraduationCap, Briefcase, Search, RefreshCw, Archive, ArchiveRestore, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useWindowed, ShowMoreBar } from "@/components/ShowMore";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useCorporate } from "./CorporateLayout";

const planNote = (plan) => {
  if (!plan?.quote) return undefined;
  const q = plan.quote;
  const changed = plan.location_count !== plan.previous_location_count;
  return `${changed ? "Plan downgraded to" : "Plan stays at"} ${q.location_count} location${q.location_count === 1 ? "" : "s"}${q.free_locations ? ` (${q.free_locations} free)` : ""} · $${q.monthly_total_usd.toFixed(2)}/mo`;
};

export default function CorporateLocations() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const { corp } = useCorporate();
  const [form, setForm] = useState({ name: "", type: "business" });
  useEffect(() => { if (corp?.kind) setForm((f) => ({ ...f, type: corp.kind })); }, [corp?.kind]);
  const [switching, setSwitching] = useState("");
  const [locQ, setLocQ] = useState("");
  const [archFor, setArchFor] = useState(null);
  const [delFor, setDelFor] = useState(null);
  const [delName, setDelName] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/corporate/dashboard")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const [addPreview, setAddPreview] = useState(null); // { current, next } quotes or { custom: true }

  // Step 1: preview the bill impact and ask for confirmation.
  const addLocation = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) return;
    setAdding(true);
    try {
      const { data: status } = await api.get("/billing/status");
      if (!status.quote) { setAddPreview({ custom: true, monthly: status.monthly_price_usd }); return; }
      const active = (data?.locations || []).filter((l) => !l.archived).length;
      const nextCount = Math.max(status.quote.location_count, active + 1);
      const next = nextCount === status.quote.location_count ? status.quote : (await api.get("/billing/corporate-quote", { params: { locations: nextCount } })).data;
      setAddPreview({ current: status.quote, next });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't calculate the new price");
    } finally {
      setAdding(false);
    }
  };

  // Step 2: confirmed — actually create the location.
  const confirmAdd = async () => {
    setAdding(true);
    try {
      await api.post("/corporate/locations", { name: form.name.trim(), type: form.type });
      toast.success(`Added ${form.name}`, { description: addPreview?.next && addPreview.next.monthly_total_usd !== addPreview.current?.monthly_total_usd ? `Your bill is now $${addPreview.next.monthly_total_usd.toFixed(2)}/mo for ${addPreview.next.location_count} locations` : undefined });
      setForm({ name: "", type: form.type });
      setAddPreview(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add location");
    } finally {
      setAdding(false);
    }
  };

  const switchTo = async (companyId, companyName) => {
    setSwitching(companyId);
    try {
      const { data } = await api.post("/corporate/switch", { company_id: companyId });
      if (data.token) localStorage.setItem("vlog_token", data.token);
      toast.success(`Switched to ${companyName}`);
      nav("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Switch failed");
    } finally {
      setSwitching("");
    }
  };

  const doArchive = async () => {
    if (!archFor || actionBusy) return;
    setActionBusy(true);
    try {
      const { data } = await api.post(`/corporate/locations/${archFor.company_id}/archive`);
      toast.success(`${archFor.company_name} archived — logins are now blocked`, { description: planNote(data.plan) });
      setArchFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Archive failed");
    } finally {
      setActionBusy(false);
    }
  };

  const doRestore = async (l) => {
    try {
      await api.post(`/corporate/locations/${l.company_id}/restore`);
      toast.success(`${l.company_name} restored`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Restore failed");
    }
  };

  const doDelete = async () => {
    if (!delFor || actionBusy) return;
    if (delName.trim() !== delFor.company_name) {
      toast.error("Type the location name exactly to confirm.");
      return;
    }
    setActionBusy(true);
    try {
      const { data } = await api.delete(`/corporate/locations/${delFor.company_id}`);
      toast.success(`${delFor.company_name} permanently deleted`, { description: planNote(data.plan) });
      setDelFor(null);
      setDelName("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setActionBusy(false);
    }
  };

  const locations = data?.locations || [];
  const lql = locQ.trim().toLowerCase();
  const visibleLocations = locations.filter((l) =>
    !lql || (l.company_name || "").toLowerCase().includes(lql) || (l.type || "").toLowerCase().includes(lql)
  );
  const locationWin = useWindowed(visibleLocations);

  return (
    <main className="px-6 md:px-10 py-8" data-testid="corporate-locations-section">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Your locations</h1>
          <p className="text-sm text-slate-600">Click <b>Open</b> to switch to that location's admin console. Add a new one below.</p>
        </div>
        <button onClick={load} data-testid="corporate-refresh-btn" title="Refresh" className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 transition">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        <form onSubmit={addLocation} className="flex flex-col sm:flex-row gap-2 px-5 py-4 bg-slate-50 border-b border-slate-200" data-testid="corporate-add-location-form">
          <input
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="New location name — e.g. North Campus"
            data-testid="corporate-new-location-name"
            className="flex-1 h-11 rounded-lg border border-slate-200 bg-white px-3.5 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
          />
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            data-testid="corporate-new-location-type"
            className="h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
          >
            {corp?.kind === "school" && <option value="school">School</option>}
            {corp?.kind === "care" && <option value="care">Home / Adult Care</option>}
            {corp?.kind === "daycare" && <option value="daycare">Daycare / Pre-K</option>}
            <option value="business">Office / Business</option>
          </select>
          <button type="submit" disabled={adding} data-testid="corporate-add-location-btn"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:shadow disabled:opacity-60 transition">
            <Plus className="h-4 w-4" /> Add location
          </button>
        </form>

        <div className="overflow-x-auto">
          <div className="flex items-center justify-between gap-3 px-5 py-2.5 border-b border-slate-100">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                value={locQ}
                onChange={(e) => setLocQ(e.target.value)}
                data-testid="corporate-locations-search-input"
                placeholder="Search locations…"
                className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-xs focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </div>
            {locQ.trim() && <span className="text-[11px] text-slate-500">{visibleLocations.length} of {locations.length}</span>}
          </div>
          <table className="w-full text-base" data-testid="corporate-locations-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-6 py-3.5">Name</th>
                <th className="px-6 py-3.5">Type</th>
                <th className="px-6 py-3.5">Inside now</th>
                <th className="px-6 py-3.5">Today</th>
                <th className="px-6 py-3.5 text-right">Open</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {locations.length === 0 && !loading && (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-500">No locations yet — add one above.</td></tr>
              )}
              {locations.length > 0 && visibleLocations.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-500">No locations match your search.</td></tr>
              )}
              {locationWin.items.map((l) => {
                const isSchool = l.type === "school";
                return (
                  <tr key={l.company_id} className={`border-t border-slate-100 hover:bg-slate-50 ${l.archived ? "opacity-60" : ""}`} data-testid={`corporate-location-${l.company_id}`}>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        {l.company_name}
                        {l.is_primary && <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Primary</span>}
                        {l.archived && <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200 text-slate-700" data-testid={`location-archived-pill-${l.company_id}`}>Archived</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                        isSchool ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"
                      }`}>
                        {isSchool ? <GraduationCap className="h-3 w-3" /> : <Briefcase className="h-3 w-3" />}
                        {isSchool ? "School" : l.type === "care" ? "Care home" : "Office"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {isSchool
                        ? <><b>{l.students_on_premises}</b> student{l.students_on_premises === 1 ? "" : "s"} · {l.visitors_inside} visitor{l.visitors_inside === 1 ? "" : "s"}</>
                        : <><b>{l.visitors_inside}</b> visitor{l.visitors_inside === 1 ? "" : "s"}</>}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {isSchool
                        ? <><b>{l.todays_student_events}</b> student event{l.todays_student_events === 1 ? "" : "s"}</>
                        : <><b>{l.todays_visitors}</b> check-in{l.todays_visitors === 1 ? "" : "s"}</>}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {l.archived ? (
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => doRestore(l)}
                            data-testid={`corporate-restore-${l.company_id}`}
                            title="Restore this location"
                            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 transition"
                          >
                            <ArchiveRestore className="h-4 w-4" /> Restore
                          </button>
                          <button
                            onClick={() => { setDelFor(l); setDelName(""); }}
                            data-testid={`corporate-delete-${l.company_id}`}
                            title="Permanently delete this location and all its data"
                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => setArchFor(l)}
                            data-testid={`corporate-archive-${l.company_id}`}
                            title="Archive this location (blocks logins, keeps data)"
                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 transition"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => switchTo(l.company_id, l.company_name)}
                            disabled={switching === l.company_id}
                            data-testid={`corporate-switch-${l.company_id}`}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 transition"
                          >
                            <ExternalLink className="h-4 w-4" /> Open
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              <ShowMoreBar win={locationWin} colSpan={5} testid="locations-show-more" />
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!addPreview} onOpenChange={(o) => { if (!o) setAddPreview(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="add-location-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Plus className="h-5 w-5 text-slate-600" /> Add {form.name.trim() || "location"}?</DialogTitle>
            <DialogDescription>
              {addPreview?.custom
                ? <>Your account is on a custom enterprise price (${Number(addPreview.monthly || 0).toFixed(2)}/mo). Adding a location won't change your bill.</>
                : addPreview?.next && addPreview?.current && addPreview.next.monthly_total_usd !== addPreview.current.monthly_total_usd
                  ? <>This adds a location to your plan and <strong className="text-slate-900">increases your bill by ${(addPreview.next.monthly_total_usd - addPreview.current.monthly_total_usd).toFixed(2)}/month</strong>.</>
                  : <>Your plan already covers {addPreview?.current?.location_count} locations — <strong className="text-slate-900">your bill doesn't change</strong>.</>}
            </DialogDescription>
          </DialogHeader>
          {addPreview?.next && addPreview?.current && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm space-y-1" data-testid="add-location-bill-preview">
              <div className="flex justify-between text-slate-600"><span>Now · {addPreview.current.location_count} location{addPreview.current.location_count === 1 ? "" : "s"}{addPreview.current.free_locations ? ` (${addPreview.current.free_locations} free)` : ""}</span><span data-testid="add-location-bill-current">${addPreview.current.monthly_total_usd.toFixed(2)}/mo</span></div>
              <div className="flex justify-between font-semibold text-slate-900"><span>After · {addPreview.next.location_count} location{addPreview.next.location_count === 1 ? "" : "s"}{addPreview.next.free_locations ? ` (${addPreview.next.free_locations} free)` : ""}</span><span data-testid="add-location-bill-next">${addPreview.next.monthly_total_usd.toFixed(2)}/mo</span></div>
              <div className="text-[11px] text-slate-500 pt-1">{addPreview.next.location_count} × ${addPreview.next.price_per_location_usd.toFixed(2)}{addPreview.next.free_locations ? ` − ${addPreview.next.free_locations} free` : ""} · billed at your next renewal</div>
            </div>
          )}
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => setAddPreview(null)} data-testid="add-location-cancel"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">Cancel</button>
            <button type="button" onClick={confirmAdd} disabled={adding} data-testid="add-location-confirm"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:shadow transition disabled:opacity-60">
              {adding && <Loader2 className="h-4 w-4 animate-spin" />} Confirm & add location
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!archFor} onOpenChange={(o) => { if (!o) setArchFor(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="archive-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Archive className="h-5 w-5 text-slate-600" /> Archive location
            </DialogTitle>
            <DialogDescription>
              Archive <strong>{archFor?.company_name}</strong>. All logins for this location will be blocked and it will stop counting in your totals — but nothing is deleted, and you can restore it anytime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => setArchFor(null)} data-testid="archive-cancel"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
              Cancel
            </button>
            <button type="button" onClick={doArchive} disabled={actionBusy} data-testid="archive-confirm"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition disabled:opacity-60">
              {actionBusy && <Loader2 className="h-4 w-4 animate-spin" />} Archive location
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delFor} onOpenChange={(o) => { if (!o) { setDelFor(null); setDelName(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="delete-location-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Permanently delete location
            </DialogTitle>
            <DialogDescription>
              This erases <strong>{delFor?.company_name}</strong> forever — every student, employee, visit log, class, bus and message for this location will be deleted. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <label className="block pt-1">
            <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Type the location name to confirm</span>
            <input
              value={delName}
              onChange={(e) => setDelName(e.target.value)}
              placeholder={delFor?.company_name}
              data-testid="delete-location-confirm-input"
              className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-red-400 focus:ring-4 focus:ring-red-100 outline-none"
            />
          </label>
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => { setDelFor(null); setDelName(""); }} data-testid="delete-location-cancel"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
              Cancel
            </button>
            <button type="button" onClick={doDelete} disabled={actionBusy || delName.trim() !== delFor?.company_name} data-testid="delete-location-confirm"
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition disabled:opacity-50">
              {actionBusy && <Loader2 className="h-4 w-4 animate-spin" />} Delete forever
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
